import { useId, useState, type ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Check, ChevronDown, Columns3, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { selectV2, setStep, setLicense } from '../v2Upload.slice';
import useDraftSync from '../useDraftSync';
import LicenseSelector from '../components/LicenseSelector';
import Panel, { PANEL_HEADER, PANEL_SHELL } from '../components/Panel';
import WizardFooter from '../components/WizardFooter';
import {
  CAPABILITY_ROWS,
  LICENSES,
  OPENNESS_TINT,
  type Caps,
} from '../utils/licenseInfo';
import {
  COMPARE_COLUMNS,
  PRESETS,
  presetFor,
  type LicensePreset,
} from '../utils/licensePresets';
import { SECTION_HEADING, STEP_HEADING } from '../components/fileCardStyles';

const TINT = OPENNESS_TINT;

function Mark({ yes }: { yes: boolean }) {
  return yes ? (
    <Check className="mx-auto h-4 w-4 text-success" />
  ) : (
    <X className="mx-auto h-4 w-4 text-destructive" />
  );
}

const ART_STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** The pictogram on each card. Plain shapes rather than icons with meanings of
 * their own — a padlock reads as "locked", which is the point. */
const PRESET_ART: Record<string, ReactElement> = {
  open: (
    <>
      <circle cx="18" cy="5" r="2.5" {...ART_STROKE} />
      <circle cx="6" cy="12" r="2.5" {...ART_STROKE} />
      <circle cx="18" cy="19" r="2.5" {...ART_STROKE} />
      <path d="m8.6 10.7 6.8-4M8.6 13.3l6.8 4" {...ART_STROKE} />
    </>
  ),
  sell: (
    <>
      <path d="M3 12V4h8l9 9-8 8-9-9Z" {...ART_STROKE} />
      <circle cx="7.5" cy="7.5" r="1.2" {...ART_STROKE} />
    </>
  ),
  personal: (
    <>
      <rect x="4" y="10" width="16" height="10" rx="2" {...ART_STROKE} />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" {...ART_STROKE} />
    </>
  ),
  custom: (
    <>
      <path d="M4 7h16M4 12h16M4 17h16" {...ART_STROKE} />
      <circle cx="9" cy="7" r="2" {...ART_STROKE} />
      <circle cx="15" cy="12" r="2" {...ART_STROKE} />
      <circle cx="8" cy="17" r="2" {...ART_STROKE} />
    </>
  ),
};

function PresetArt({ id, selected }: { id: string; selected: boolean }) {
  const tone = selected ? 'text-primary' : 'text-muted-foreground';
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={cn('h-9 w-9', tone)}>
      {PRESET_ART[id]}
    </svg>
  );
}

/** The licence a card names: its own, or — for Custom, which has none — the one
 * actually saved. Only while Custom is the active choice: the saved licence is
 * also the selected preset's own code, so an idle Custom would echo CC BY-SA
 * beside the card already claiming it. */
function cardCode(
  preset: LicensePreset,
  activeId: string,
  saved: string
): string | undefined {
  if (preset.code) return preset.code;
  return preset.id === activeId ? saved : undefined;
}

function PresetCard({
  preset,
  selected,
  code,
}: {
  preset: LicensePreset;
  selected: boolean;
  /** The licence this card names, already resolved by `cardCode`. */
  code?: string;
}) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={cn(
        'relative flex cursor-pointer flex-col items-center gap-2 rounded-xl border p-5 text-center transition-colors duration-fast',
        selected
          ? 'border-primary bg-accent'
          : 'border-border bg-card hover:bg-muted/40'
      )}
    >
      <RadioGroupItem
        id={id}
        value={preset.id}
        aria-label={preset.title}
        className="absolute left-4 top-4"
      />
      <PresetArt id={preset.id} selected={selected} />
      <span className="flex flex-col gap-0.5">
        <span className="font-secondary text-[15px] font-semibold">
          {preset.title}
        </span>
        {code && (
          <span className="font-tertiary text-[11px] font-normal text-muted-foreground">
            {code}
          </span>
        )}
      </span>
      <span className="text-xs leading-relaxed text-muted-foreground">
        {preset.blurb}
      </span>
      <span className="mt-auto flex flex-wrap items-center justify-center gap-1.5 pt-1">
        <span
          className={cn(
            'rounded-full px-2.5 py-1 text-[11px] font-tertiary',
            TINT[preset.tag]
          )}
        >
          {preset.tag}
        </span>
        <span
          className={cn(
            'rounded-full px-2.5 py-1 text-[11px] font-tertiary',
            TINT[preset.tag]
          )}
        >
          {preset.audience}
        </span>
      </span>
    </label>
  );
}

/** The four levels side by side. The Custom column answers for the CC license
 * currently chosen under Technical details, so it is a real comparison rather
 * than a column of "your choice". */
/** Only the Custom column names a licence under its title — the other three
 * ARE one. */
/** The licence a column actually saves, under its plain-language name. Custom
 * has no code of its own, so it shows whichever one is picked. */
function CustomCode({ preset, code }: { preset: LicensePreset; code: string }) {
  const shown = preset.code ?? code;
  if (!shown) return null;
  return (
    <span className="mt-0.5 block text-[11px] font-normal opacity-80">
      ({shown})
    </span>
  );
}

/** A preset states its own rules; Custom borrows the chosen CC licence's, and
 * answers "your choice" only while no licence is chosen at all. */
function Answer({ caps, row }: { caps?: Caps; row: keyof Caps }) {
  if (!caps) {
    return (
      <span className="text-[11px] text-muted-foreground">Your choice</span>
    );
  }
  return <Mark yes={caps[row]} />;
}

/** The licence's full name, or its bare code when it is one we don't list. */
const licenseName = (code: string) =>
  LICENSES.find((l) => l.code === code)?.name ?? code;

function CompareTable({
  selectedId,
  customCode,
}: {
  selectedId: string;
  customCode: string;
}) {
  const custom = LICENSES.find((l) => l.code === customCode);
  return (
    <div data-focus="license-compare">
      <Panel
        icon={Columns3}
        title="Compare all license options"
        help="Your pick is the highlighted column. Custom follows whichever Creative Commons license you choose below."
        bodyClassName="overflow-x-auto p-0"
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[220px]">
                What can users do?
              </TableHead>
              {COMPARE_COLUMNS.map(({ preset }) => (
                <TableHead
                  key={preset.id}
                  className={cn(
                    'text-center font-tertiary',
                    preset.id === selectedId &&
                      'bg-primary font-semibold text-primary-foreground'
                  )}
                >
                  {preset.title}
                  <CustomCode
                    preset={preset}
                    code={custom?.code ?? customCode}
                  />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {CAPABILITY_ROWS.map((row) => (
              <TableRow key={row.key}>
                <TableCell className="text-xs">{row.label}</TableCell>
                {COMPARE_COLUMNS.map(({ preset, caps }) => (
                  <TableCell
                    key={preset.id}
                    className={cn(
                      'text-center',
                      preset.id === selectedId && 'bg-primary/15'
                    )}
                  >
                    <Answer caps={caps ?? custom?.caps} row={row.key} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
    </div>
  );
}

/** The seven CC licenses, for the user who knows which one they want. Open by
 * default under Custom, and available from the footer either way. */
function TechnicalDetails({
  open,
  selected,
  onToggle,
  onSelect,
}: {
  open: boolean;
  selected: string;
  onToggle: () => void;
  onSelect: (code: string) => void;
}) {
  return (
    <div className={PANEL_SHELL} data-focus="license-cc">
      <button
        type="button"
        onClick={onToggle}
        className={cn(PANEL_HEADER, 'w-full gap-3 text-left')}
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary text-[10px] font-semibold text-primary">
          CC
        </span>
        <span className={SECTION_HEADING}>
          Technical details: Creative Commons license reference
        </span>
        <span className="grow" />
        <span className="text-[11px] font-tertiary text-muted-foreground">
          {licenseName(selected)}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-fast',
            open && 'rotate-180'
          )}
        />
      </button>
      {open && (
        <div className="border-t p-5">
          <LicenseSelector selected={selected} onSelect={onSelect} />
        </div>
      )}
    </div>
  );
}

export default function LicenseStep() {
  const dispatch = useDispatch();
  const { basics } = useSelector(selectV2);
  const { syncLicense } = useDraftSync();
  const [saving, setSaving] = useState(false);

  const preset = presetFor(basics.license);
  // Custom is not a license of its own — it is the full list, so picking it
  // shows the list and the code stays whatever it already was.
  const [showAll, setShowAll] = useState(preset.id === 'custom');

  const choose = (id: string) => {
    const picked = PRESETS.find((p) => p.id === id);
    if (picked?.code) {
      dispatch(setLicense(picked.code));
      setShowAll(false);
      return;
    }
    setShowAll(true);
  };

  const handleContinue = async () => {
    setSaving(true);
    const ok = await syncLicense();
    setSaving(false);
    if (ok) dispatch(setStep(6));
  };

  const activeId = showAll ? 'custom' : preset.id;

  return (
    <div className="flex flex-col min-h-full">
      <h2 className={STEP_HEADING}>Choose how others can use your project</h2>
      <p className="text-sm text-muted-foreground mb-6 max-w-[640px]">
        Select the level of access you want to give. You can always change this
        later.
      </p>

      <div className="max-w-[1400px] space-y-6" data-focus="license">
        <RadioGroup
          value={activeId}
          onValueChange={choose}
          data-focus="license-presets"
          className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 xl:grid-cols-4"
        >
          {PRESETS.map((p) => (
            <PresetCard
              key={p.id}
              preset={p}
              selected={activeId === p.id}
              code={cardCode(p, activeId, basics.license)}
            />
          ))}
        </RadioGroup>

        <CompareTable selectedId={activeId} customCode={basics.license} />

        <TechnicalDetails
          open={showAll}
          selected={basics.license}
          onToggle={() => setShowAll((v) => !v)}
          onSelect={(code) => dispatch(setLicense(code))}
        />
      </div>

      <WizardFooter
        onBack={() => dispatch(setStep(4))}
        onContinue={handleContinue}
        busy={saving}
        busyLabel="Saving…"
        backDisabled={saving}
        continueDisabled={false}
      />
    </div>
  );
}
